import type { ReactNode } from 'react'
import type { BlasonPalette } from '../registry'

/**
 * Blason : formes, palettes, emblèmes (glyphs) et champs. ViewBox 0-120,
 * écu ≈ 24-96 × 16-107, centre optique de l'emblème ≈ (60,70) sous le chef
 * (y ≤ 40). Les glyphs/fields reçoivent la palette — ne JAMAIS coder en dur
 * une couleur d'emblème, utiliser p.glyph / p.accent.
 */
export const BLASON_SHAPES: Record<string, { outline: string; chief: string }> = {
  shape_ecu_pointe: {
    outline: 'M 24 16 H 96 V 56 C 96 80 82 97 60 107 C 38 97 24 80 24 56 Z',
    chief: 'M 24 16 H 96 V 40 H 24 Z',
  },
  shape_ecu_arrondi: {
    outline: 'M 24 24 Q 24 16 32 16 H 88 Q 96 16 96 24 V 70 C 96 89.9 79.9 106 60 106 C 40.1 106 24 89.9 24 70 Z',
    chief: 'M 24 40 V 24 Q 24 16 32 16 H 88 Q 96 16 96 24 V 40 Z',
  },
  shape_fanion: {
    outline: 'M 24 16 H 96 L 60 110 Z',
    chief: 'M 24 16 H 96 L 86.8 40 H 33.2 Z',
  },
  shape_banniere: {
    outline: 'M 26 16 H 94 V 96 L 60 84 L 26 96 Z',
    chief: 'M 26 16 H 94 V 40 H 26 Z',
  },
}

export const BLASON_PALETTES: Record<string, BlasonPalette> = {
  pal_ocean_or: {
    field: ['#1E96B5', '#0A3E4F'],
    chief: ['#F0DCA8', '#C9A55C'],
    glyph: '#F5EDD7',
    accent: '#E5CC8F',
  },
  pal_rhum_sable: {
    field: ['#7A3E1F', '#4A2210'],
    chief: ['#F5EDD7', '#E5CC8F'],
    glyph: '#F5EDD7',
    accent: '#E5CC8F',
  },
  pal_nuit_ivoire: {
    field: ['#12222E', '#060D12'],
    chief: ['#F5EDD7', '#D8C9A3'],
    glyph: '#F5EDD7',
    accent: '#C9A55C',
  },
  pal_ecume: {
    field: ['#5FBDD0', '#1E96B5'],
    chief: ['#F5EDD7', '#BFE4ED'],
    glyph: '#0A3E4F',
    accent: '#F5EDD7',
  },
  pal_emeraude: {
    field: ['#1D9E75', '#085041'],
    chief: ['#F0DCA8', '#C9A55C'],
    glyph: '#F5EDD7',
    accent: '#9FE1CB',
  },
  pal_sang_ebene: {
    field: ['#8C2B2B', '#3D0F0F'],
    chief: ['#1E2430', '#0B0F16'],
    glyph: '#F5EDD7',
    accent: '#C9A55C',
  },
  pal_royal_marine: {
    field: ['#1F4FA0', '#0C2C5E'],
    chief: ['#F0DCA8', '#C9A55C'],
    glyph: '#F5EDD7',
    accent: '#8FB8E8',
  },
  pal_or_pourpre: {
    field: ['#5E1F5E', '#2E0B32'],
    chief: ['#F0DCA8', '#C9A55C'],
    glyph: '#F5CD6B',
    accent: '#E5CC8F',
  },
}

export const BLASON_GLYPHS: Record<string, (palette: BlasonPalette) => ReactNode> = {
  glyph_ancre: (p) => (
    <g fill="none" stroke={p.glyph} strokeWidth="4.4" strokeLinecap="round">
      <circle cx="60" cy="52" r="4.4" />
      <path d="M 60 56.4 V 88" />
      <path d="M 46 62 H 74" />
      <path d="M 42 74 C 45 85 51.5 90.5 60 91 C 68.5 90.5 75 85 78 74" />
      <path d="M 42 74 l -4.5 4 M 78 74 l 4.5 4" strokeWidth="3.6" />
    </g>
  ),
  glyph_rose_vents: (p) => (
    <g strokeLinejoin="round">
      <g fill={p.accent}>
        <path d="M 71 57 L 65.5 65.7 L 60 68 L 62.3 62.5 Z" />
        <path d="M 49 57 L 54.5 65.7 L 60 68 L 57.7 62.5 Z" />
        <path d="M 71 79 L 65.5 70.3 L 60 68 L 62.3 73.5 Z" />
        <path d="M 49 79 L 54.5 70.3 L 60 68 L 57.7 73.5 Z" />
      </g>
      <g fill={p.glyph}>
        <path d="M 60 45 L 65 62.5 L 60 68 L 55 62.5 Z" />
        <path d="M 60 91 L 65 73.5 L 60 68 L 55 73.5 Z" />
        <path d="M 83 68 L 65.5 73 L 60 68 L 65.5 63 Z" />
        <path d="M 37 68 L 54.5 73 L 60 68 L 54.5 63 Z" />
      </g>
      <circle cx="60" cy="68" r="4.5" fill={p.glyph} />
      <circle cx="60" cy="68" r="2" fill={p.accent} />
    </g>
  ),
  glyph_trident: (p) => (
    <g>
      <g fill="none" stroke={p.glyph} strokeWidth="4.2" strokeLinecap="round">
        <path d="M 60 90 V 53" />
        <path d="M 47 66 H 73" />
        <path d="M 47 66 C 46.5 61 47.5 56 50 52.5" />
        <path d="M 73 66 C 73.5 61 72.5 56 70 52.5" />
      </g>
      <g fill={p.glyph} strokeLinejoin="round">
        <path d="M 60 44 L 63.5 49.5 L 60 55 L 56.5 49.5 Z" />
        <path d="M 51.5 45.5 L 54.5 50 L 51.5 54.5 L 48.5 50 Z" />
        <path d="M 68.5 45.5 L 71.5 50 L 68.5 54.5 L 65.5 50 Z" />
      </g>
      <circle cx="60" cy="90" r="2.6" fill={p.accent} />
    </g>
  ),
  glyph_voilier: (p) => (
    <g>
      <path d="M 60 46 V 76" fill="none" stroke={p.glyph} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M 60 47 C 68 54.5 74 62.5 77 70 L 60 70 Z" fill={p.glyph} />
      <path d="M 57 51 C 52.5 57 48.5 63.5 46 70 L 57 70 Z" fill={p.accent} />
      <path d="M 60 46 L 66.5 48 L 60 50 Z" fill={p.accent} />
      <path d="M 41 72 C 44 80 51 84 60 84 C 69 84 76 80 79 72 C 72 75.5 66 76.5 60 76.5 C 54 76.5 48 75.5 41 72 Z" fill={p.glyph} />
      <path d="M 42 88.5 Q 49 84.5 56 88.5 Q 63 92.5 70 88.5" fill="none" stroke={p.glyph} strokeWidth="3" strokeLinecap="round" />
    </g>
  ),
  glyph_crane: (p) => (
    <g>
      <g stroke={p.glyph} strokeWidth="4.5" strokeLinecap="round">
        <path d="M 44 52 L 76 84" />
        <path d="M 76 52 L 44 84" />
      </g>
      <path d="M 46.5 61 C 46.5 52.5 52.5 47 60 47 C 67.5 47 73.5 52.5 73.5 61 C 73.5 67 70.5 71 66 72.5 L 54 72.5 C 49.5 71 46.5 67 46.5 61 Z" fill={p.glyph} />
      <path d="M 54 72 H 66 V 80 C 66 81.5 64.8 82.5 63.3 82.5 H 56.7 C 55.2 82.5 54 81.5 54 80 Z" fill={p.glyph} />
      <g stroke={p.field[1]} strokeWidth="1.6" strokeLinecap="round">
        <path d="M 57 73.5 V 81" />
        <path d="M 60 73.5 V 81.5" />
        <path d="M 63 73.5 V 81" />
      </g>
      <circle cx="54.5" cy="60" r="4" fill={p.field[1]} />
      <circle cx="65.5" cy="60" r="4" fill={p.field[1]} />
      <path d="M 60 64.5 L 62.3 69.5 L 57.7 69.5 Z" fill={p.field[1]} />
      <circle cx="54.5" cy="60" r="1.3" fill={p.accent} />
      <circle cx="65.5" cy="60" r="1.3" fill={p.accent} />
    </g>
  ),
  glyph_epees: (p) => (
    <g>
      <g fill="none" stroke={p.glyph} strokeLinecap="round">
        <path d="M 48 84 L 72 48" strokeWidth="4" />
        <path d="M 72 84 L 48 48" strokeWidth="4" />
        <path d="M 47.5 75.7 L 55.6 81.2" strokeWidth="3.5" />
        <path d="M 72.5 75.7 L 64.4 81.2" strokeWidth="3.5" />
      </g>
      <circle cx="46.5" cy="86.2" r="2.8" fill={p.accent} />
      <circle cx="73.5" cy="86.2" r="2.8" fill={p.accent} />
    </g>
  ),
  glyph_balance: (p) => (
    <g>
      <g fill="none" stroke={p.glyph} strokeLinecap="round">
        <path d="M 60 52 V 88" strokeWidth="4" />
        <path d="M 50 88 H 70" strokeWidth="4" />
        <path d="M 42 54 H 78" strokeWidth="4" />
        <path d="M 44 55 L 39 68 M 44 55 L 49 68" strokeWidth="2.5" />
        <path d="M 76 55 L 71 68 M 76 55 L 81 68" strokeWidth="2.5" />
      </g>
      <path d="M 37 68 Q 44 78 51 68 Z" fill={p.glyph} />
      <path d="M 69 68 Q 76 78 83 68 Z" fill={p.glyph} />
      <circle cx="60" cy="50.5" r="2.6" fill={p.accent} />
    </g>
  ),
  glyph_lanterne: (p) => (
    <g>
      <circle cx="60" cy="47" r="3.5" fill="none" stroke={p.glyph} strokeWidth="3" />
      <path d="M 60 50.5 V 54" fill="none" stroke={p.glyph} strokeWidth="3" strokeLinecap="round" />
      <path d="M 60 56 L 66.5 60 L 66.5 78 L 60 82 L 53.5 78 L 53.5 60 Z" fill={p.accent} opacity="0.5" />
      <path d="M 60 52 L 70 58 L 70 80 L 60 86 L 50 80 L 50 58 Z" fill="none" stroke={p.glyph} strokeWidth="3.8" strokeLinejoin="round" />
      <path d="M 60 62.5 C 63.2 66.8 64 70 60 73.8 C 56 70 56.8 66.8 60 62.5 Z" fill={p.accent} />
      <path d="M 52 89 H 68" fill="none" stroke={p.glyph} strokeWidth="3.5" strokeLinecap="round" />
    </g>
  ),
  glyph_kraken: (p) => (
    <g>
      <g fill="none" stroke={p.glyph} strokeWidth="3.8" strokeLinecap="round">
        <path d="M 48.5 67.5 C 43 72 39.5 78 42 83 C 43.8 86.4 47.6 86 48.6 82.8" />
        <path d="M 54 70 C 52.5 77 50.5 83 45.5 87.5" />
        <path d="M 60 70 C 60 78 58.5 84.5 55 89" />
        <path d="M 66 70 C 67.5 77 69.5 83 74.5 87.5" />
        <path d="M 71.5 67.5 C 77 72 80.5 78 78 83 C 76.2 86.4 72.4 86 71.4 82.8" />
      </g>
      <path d="M 46 62 C 46 51 52 45.5 60 45.5 C 68 45.5 74 51 74 62 C 74 66 72.5 68.5 70 70 L 50 70 C 47.5 68.5 46 66 46 62 Z" fill={p.glyph} />
      <circle cx="54.5" cy="60" r="3.2" fill={p.field[1]} />
      <circle cx="65.5" cy="60" r="3.2" fill={p.field[1]} />
      <g fill={p.accent}>
        <circle cx="44.5" cy="79.5" r="1.5" />
        <circle cx="52" cy="80.5" r="1.5" />
        <circle cx="60" cy="80" r="1.5" />
        <circle cx="68" cy="80.5" r="1.5" />
      </g>
    </g>
  ),
  glyph_etoile_polaire: (p) => (
    <g strokeLinejoin="round">
      <circle cx="60" cy="68" r="18" fill="none" stroke={p.accent} strokeWidth="1.5" opacity="0.5" />
      <g fill={p.accent}>
        <path d="M 68.5 59.5 L 64.6 66.2 L 60 68 L 61.8 63.4 Z" />
        <path d="M 51.5 59.5 L 55.4 66.2 L 60 68 L 58.2 63.4 Z" />
        <path d="M 68.5 76.5 L 64.6 69.8 L 60 68 L 61.8 72.6 Z" />
        <path d="M 51.5 76.5 L 55.4 69.8 L 60 68 L 58.2 72.6 Z" />
      </g>
      <g fill={p.glyph}>
        <path d="M 60 45 L 63.5 64.5 L 60 68 L 56.5 64.5 Z" />
        <path d="M 60 91 L 63.5 71.5 L 60 68 L 56.5 71.5 Z" />
        <path d="M 83 68 L 63.5 71.5 L 60 68 L 63.5 64.5 Z" />
        <path d="M 37 68 L 56.5 71.5 L 60 68 L 56.5 64.5 Z" />
      </g>
      <circle cx="60" cy="68" r="1.8" fill={p.accent} />
    </g>
  ),
}

export const BLASON_FIELDS: Record<string, (palette: BlasonPalette) => ReactNode> = {
  field_uni: () => null,
  field_raye: (p) => (
    <g stroke={p.accent} strokeWidth="3" opacity="0.16">
      <path d="M 24 52 H 96 M 24 66 H 96 M 24 80 H 96 M 30 94 H 90" />
    </g>
  ),
  field_vagues: (p) => (
    <g fill="none" stroke={p.accent} strokeWidth="2.5" strokeLinecap="round" opacity="0.18">
      <path d="M 28 55 Q 32 51.5 36 55 T 44 55 T 52 55 T 60 55 T 68 55 T 76 55 T 84 55 T 92 55" />
      <path d="M 32 72 Q 36 68.5 40 72 T 48 72 T 56 72 T 64 72 T 72 72 T 80 72 T 88 72" />
      <path d="M 36 89 Q 40 85.5 44 89 T 52 89 T 60 89 T 68 89 T 76 89 T 84 89" />
    </g>
  ),
  field_besants: (p) => (
    <g fill={p.accent} opacity="0.15">
      <circle cx="46" cy="48" r="3.5" />
      <circle cx="74" cy="48" r="3.5" />
      <circle cx="32" cy="66" r="3.5" />
      <circle cx="60" cy="66" r="3.5" />
      <circle cx="88" cy="66" r="3.5" />
      <circle cx="46" cy="84" r="3.5" />
      <circle cx="74" cy="84" r="3.5" />
    </g>
  ),
}
